import { type ReactElement, useEffect, useRef, useState } from "react";

import { PlaceholderPicker } from "@/components/PlaceholderPicker";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  type ActionOutcome,
  type ConsentTemplate,
  type ConsentType,
  LOAD_FAILED,
  approveConsentTemplate,
} from "@/lib/api";

/** The id of a kind's wording field, the focus target after an approval. */
export function wordingEditorId(kind: ConsentType): string {
  return `wortlaut-editor-${kind}`;
}

/**
 * The wording of one consent kind in the form view's panel, for whoever may approve wording
 * (D37, form part; D52). It works on the template behind the preview's text, with its
 * placeholders; the preview shows the server's rendering, never one composed here.
 *
 * **Approving creates a new version of the cell's template**, the way the master data does
 * (`approveConsentTemplate`): it is not a text of this action, so it never joins the form's save
 * bar and applies to every action at once. A consent granted earlier keeps the wording it copied
 * (concept 3.2); only boxes offered from now on read the new one. The host keys the editor by
 * the template's version, so a new version starts it afresh.
 */
export function ConsentWordingEditor({
  kind,
  template,
  focusOnMount = false,
  onApproved,
}: {
  readonly kind: ConsentType;
  /** The current version of this kind. */
  readonly template: ConsentTemplate;
  /** Take the focus: after an approval, and again when the new version replaces the editor. */
  readonly focusOnMount?: boolean;
  /** After the approval: the host reloads the templates and the preview. */
  readonly onApproved: () => void;
}): ReactElement {
  const current = template.body_template ?? "";
  const [text, setText] = useState(current);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const field = useRef<HTMLTextAreaElement>(null);
  const id = wordingEditorId(kind);

  // When it is set, and again on mount: the new version is a new editor (keyed by the version).
  useEffect(() => {
    if (focusOnMount) {
      document.getElementById(id)?.focus();
    }
  }, [focusOnMount, id]);

  const approve = (): void => {
    setBusy(true);
    setError(null);
    approveConsentTemplate(kind, text.trim())
      .then((outcome: ActionOutcome) => {
        if (outcome.ok) {
          console.log("[ConsentWordingEditor] approved", kind);
          onApproved();
        } else {
          setError(outcome.message);
        }
      })
      .catch((caught: unknown) => {
        console.error("[ConsentWordingEditor]", caught);
        setError(LOAD_FAILED);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <div className="mt-6">
      <Label htmlFor={id}>Wortlaut</Label>
      <Textarea
        ref={field}
        id={id}
        className="mt-1.5 bg-background"
        rows={6}
        maxLength={20000}
        value={text}
        // Read-only rather than disabled: the focus may stay here while the approval runs.
        readOnly={busy}
        onChange={(event) => {
          setText(event.target.value);
        }}
      />
      <PlaceholderPicker
        names={template.allowed_placeholders}
        field={field}
        value={text}
        onChange={setText}
        disabled={busy}
      />
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Button
          type="button"
          size="sm"
          aria-describedby={`${id}-hinweis`}
          disabled={busy || text.trim() === "" || text.trim() === current.trim()}
          onClick={approve}
        >
          {busy ? "Wird freigegeben …" : "Neue Fassung freigeben"}
        </Button>
        <p id={`${id}-hinweis`} className="text-sm text-muted-foreground">
          Gilt für alle Aktionen.
        </p>
      </div>
      {error === null ? null : (
        <p className="mt-2 text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
